"use client";

import { useEffect, useId, useMemo, useRef } from "react";
import type { ExternalEmbed } from "@/lib/external-embed";
import { cn } from "@/lib/utils";

const GETTY_WIDGET_SRC = "https://embed-cdn.gettyimages.com/widgets.js";
const GETTY_IFRAME_HOST = "embed.gettyimages.com";
const DEFAULT_GETTY_WIDTH = 594;
const DEFAULT_GETTY_HEIGHT = 396;

type GettyWidgetLoadConfig = {
  id: string;
  sig?: string;
  items: string;
  w: string;
  h: string;
  caption: boolean;
  tld: string;
  is360: boolean;
};

declare global {
  interface Window {
    gie?: {
      widgets?: {
        load?: (config: GettyWidgetLoadConfig) => void;
      };
    };
  }
}

let gettyScriptPromise: Promise<void> | null = null;

function gettyDebug(message: string, payload?: unknown) {
  if (process.env.NODE_ENV !== "production") console.info(`[GettyEmbed] ${message}`, payload ?? "");
}

function loadGettyWidgetScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.gie?.widgets?.load) {
    gettyDebug("script already ready");
    return Promise.resolve();
  }
  if (gettyScriptPromise) return gettyScriptPromise;

  gettyScriptPromise = new Promise((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]")).find((script) => {
      try {
        return new URL(script.src).toString() === GETTY_WIDGET_SRC;
      } catch {
        return false;
      }
    });
    if (existing) {
      if (existing.dataset.gettyLoaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => {
        existing.dataset.gettyLoaded = "true";
        gettyDebug("script loaded");
        resolve();
      }, { once: true });
      existing.addEventListener("error", () => reject(new Error("Getty widget script failed to load.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = GETTY_WIDGET_SRC;
    script.async = true;
    script.charset = "utf-8";
    script.addEventListener("load", () => {
      script.dataset.gettyLoaded = "true";
      gettyDebug("script loaded");
      resolve();
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Getty widget script failed to load.")), { once: true });
    document.head.appendChild(script);
  });

  return gettyScriptPromise;
}

function safeDimension(value: number | null | undefined, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;
}

function gettyDetailUrl(itemId: string) {
  return `https://www.gettyimages.com/detail/${encodeURIComponent(itemId)}`;
}

function normalizeAnchorId(input: string | null | undefined) {
  const value = input?.trim();
  if (!value) return null;
  return /^[a-zA-Z][\w:-]{0,120}$/.test(value) ? value : null;
}

function deterministicAnchorId(itemId: string, reactId: string) {
  const suffix = reactId.replace(/[^a-zA-Z0-9_-]/g, "");
  return `getty-${itemId}${suffix ? `-${suffix}` : ""}`;
}

function normalizeGettyIframeSrc(input: string | null | undefined, itemId: string) {
  const value = input?.trim();
  if (!value || /^javascript:/i.test(value)) return null;
  try {
    const url = new URL(value.startsWith("//") ? `https:${value}` : value);
    url.protocol = "https:";
    if (url.hostname.toLowerCase() !== GETTY_IFRAME_HOST) return null;
    const id = url.pathname.match(/^\/embed\/(\d+)(?:\/|$)/)?.[1];
    if (!id || id !== itemId) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function GettyEmbed({
  iframeSrc,
  anchorId,
  sig,
  itemId,
  width,
  height,
  caption = true,
  tld = "com",
  is360 = false,
  className,
}: {
  iframeSrc?: string | null;
  anchorId?: string | null;
  sig?: string | null;
  itemId: string;
  width?: number | null;
  height?: number | null;
  caption?: boolean | null;
  tld?: string | null;
  is360?: boolean | null;
  className?: string;
}) {
  const reactId = useId();
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const initializedKeyRef = useRef<string | null>(null);
  const naturalWidth = safeDimension(width, DEFAULT_GETTY_WIDTH);
  const naturalHeight = safeDimension(height, DEFAULT_GETTY_HEIGHT);
  const aspectRatio = `${naturalWidth} / ${naturalHeight}`;
  const widgetId = useMemo(() => normalizeAnchorId(anchorId) ?? deterministicAnchorId(itemId, reactId), [anchorId, itemId, reactId]);
  const safeIframeSrc = useMemo(() => normalizeGettyIframeSrc(iframeSrc, itemId), [iframeSrc, itemId]);
  const widgetTld = tld?.trim() || "com";
  const widgetCaption = caption ?? true;
  const widgetIs360 = is360 ?? false;

  useEffect(() => {
    if (safeIframeSrc) return;
    if (!anchorRef.current || !/^\d+$/.test(itemId)) return;
    if (!widgetId) return;
    if (!sig) gettyDebug("missing sig", { id: widgetId, itemId });

    let cancelled = false;
    const initKey = JSON.stringify({ widgetId, sig, itemId, naturalWidth, naturalHeight, widgetCaption, widgetTld, widgetIs360 });
    if (initializedKeyRef.current === initKey) return;

    loadGettyWidgetScript()
      .then(() => {
        if (cancelled) return;
        const load = () => window.gie?.widgets?.load;
        const config: GettyWidgetLoadConfig = {
          id: widgetId,
          sig: sig ?? undefined,
          items: itemId,
          w: `${naturalWidth}px`,
          h: `${naturalHeight}px`,
          caption: widgetCaption,
          tld: widgetTld,
          is360: widgetIs360,
        };
        const loadWidget = load();
        if (loadWidget) {
          initializedKeyRef.current = initKey;
          gettyDebug("widgets.load called", config);
          loadWidget(config);
          return;
        }
        window.setTimeout(() => {
          if (cancelled) return;
          const retryLoadWidget = load();
          if (!retryLoadWidget) {
            gettyDebug("missing widgets.load after retry", { id: widgetId, itemId });
            return;
          }
          initializedKeyRef.current = initKey;
          gettyDebug("widgets.load called", config);
          retryLoadWidget(config);
        }, 50);
      })
      .catch(() => {
        gettyDebug("script failed", { id: widgetId, itemId });
      });

    return () => {
      cancelled = true;
    };
  }, [itemId, naturalHeight, naturalWidth, safeIframeSrc, sig, widgetCaption, widgetId, widgetIs360, widgetTld]);

  if (safeIframeSrc) {
    return (
      <div
        className={cn("getty-embed h-full w-full overflow-hidden bg-[#FCFBF8]", className)}
        style={{ aspectRatio }}
        data-getty-item-id={itemId}
        data-getty-render-mode="iframe"
      >
        <iframe
          src={safeIframeSrc}
          title="Getty Images embed"
          className="block h-full w-full"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          allow="fullscreen; clipboard-write"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div
      className={cn("getty-embed h-full w-full overflow-hidden bg-[#FCFBF8]", className)}
      style={{ aspectRatio }}
      data-getty-item-id={itemId}
      data-getty-anchor-id={widgetId}
    >
      <a
        ref={anchorRef}
        id={widgetId}
        className="gie-single"
        href={gettyDetailUrl(itemId)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Embed from Getty Images
      </a>
    </div>
  );
}

export function GettyExternalEmbed({
  embed,
  anchorId,
  sig,
  itemId,
  width,
  height,
  caption,
  tld,
  is360,
  className,
}: {
  embed: Pick<ExternalEmbed, "embedUrl" | "renderMode" | "anchorId" | "sig" | "itemId" | "width" | "height" | "caption" | "tld" | "is360">;
  anchorId?: string | null;
  sig?: string | null;
  itemId?: string | null;
  width?: number | null;
  height?: number | null;
  caption?: boolean | null;
  tld?: string | null;
  is360?: boolean | null;
  className?: string;
}) {
  const resolvedItemId = embed.itemId ?? itemId;
  if (!resolvedItemId) return null;

  return (
    <GettyEmbed
      iframeSrc={embed.renderMode === "iframe" ? embed.embedUrl : undefined}
      anchorId={embed.anchorId ?? anchorId}
      sig={embed.sig ?? sig}
      itemId={resolvedItemId}
      width={embed.width ?? width}
      height={embed.height ?? height}
      caption={typeof embed.caption === "boolean" ? embed.caption : caption}
      tld={embed.tld ?? tld}
      is360={typeof embed.is360 === "boolean" ? embed.is360 : is360}
      className={className}
    />
  );
}
